import { Schemas } from "@kollectivia/shared";
/**
 * IntakeFormFill — remplissage public d'un formulaire versionné (9.U.5).
 *
 * Rendu quand le GET public retourne `kind:"form"` (lien magic versionné). Le
 * client remplit `<FormRenderer mode="fill">` + coche le consentement, puis POST
 * JSON `{ data, consent, submission_id }` via `useSubmitVersionedIntake`.
 *
 * Idempotence : `submission_id` est généré UNE fois au montage (ref stable) →
 * un retry (nouveau clic après erreur réseau) réémet la MÊME clé → le serveur
 * renvoie le même `intake_event_id` sans re-consommer le quota. Le consentement
 * n'entre PAS dans la comparaison de collision (data-only côté serveur), donc un
 * timestamp variable au retry ne provoque jamais de faux conflit.
 *
 * Anti-leak : aucune métadonnée projet/tenant n'est exposée (le GET versionné ne
 * renvoie que kind/language/schema). Toute défaillance token-level → onTokenInvalid.
 */
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FormRenderer, type RenderSchema } from "../../../components/forms/FormRenderer";
import {
  useSubmitVersionedIntake,
  type VersionedSubmissionResult,
} from "../../../lib/queries/public-intake";
import {
  type FieldIssue,
  messagesDIssues,
  toWireData,
  validateSubmitData,
} from "../../form-templates/_parts/fillData";

import { ConsentCheckbox } from "./ConsentCheckbox";

type FormSchema = Parameters<typeof toWireData>[1];

type Props = {
  readonly token: string;
  readonly schema: Record<string, unknown>;
  readonly language: "fr" | "en";
  /** 9.BJ.3 — valeurs suggérées par le serveur, absentes quand rien n'est connu. */
  readonly prefill?: Readonly<Record<string, string>>;
  readonly onSuccess: (intakeEventId: string) => void;
  readonly onTokenInvalid: () => void;
};

export function IntakeFormFill({
  token,
  schema,
  language,
  prefill,
  onSuccess,
  onTokenInvalid,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const signingMandate = Schemas.hasSignatureFields(schema);

  // Le schéma jsonb de la version sert à la fois de RenderSchema (rendu) et de
  // FormSchema (normalisation + validation submit) — même shape (cf. FormFillPage).
  const renderSchema = schema as unknown as RenderSchema;
  const formSchema = schema as unknown as FormSchema;

  // Clé d'idempotence — stable pour toute la durée de vie du montage.
  const submissionIdRef = useRef<string>(crypto.randomUUID());

  /**
   * 9.BJ.3 — l'état part des valeurs SUGGÉRÉES par le serveur.
   *
   * Amorçage à l'initialisation, pas dans un effet : une suggestion qui
   * arriverait après une frappe écraserait ce que la personne vient d'écrire.
   * Le web ne choisit rien — il reçoit une carte `{ clé → valeur }` et la pose
   * telle quelle ; les champs restent ordinaires, donc corrigibles.
   */
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...(prefill ?? {}) }));
  const [fieldErrors, setFieldErrors] = useState<Record<string, FieldIssue>>({});
  const [consent, setConsent] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const submit = useSubmitVersionedIntake(token);
  const busy = submit.isPending;

  const dispatch = (result: VersionedSubmissionResult) => {
    switch (result.kind) {
      case "received":
        onSuccess(result.intakeEventId);
        return;
      case "invalid":
        onTokenInvalid();
        return;
      case "payload":
        // Le serveur (source de vérité) a rejeté le payload — re-valide localement
        // pour re-afficher les erreurs par champ.
        setFieldErrors(validateSubmitData(toWireData(values, formSchema), formSchema));
        setBanner(t("public_intake.form.versioned.fix_errors"));
        return;
      case "conflict":
        setBanner(t("public_intake.form.versioned.conflict"));
        return;
      case "rate_limited":
        setBanner(t("public_intake.form.versioned.rate_limited"));
        return;
      case "internal":
        setBanner(t("public_intake.form.versioned.generic_error"));
        return;
    }
  };

  const onSubmit = () => {
    if (busy) return;
    setAttempted(true);
    setBanner(null);

    const wire = toWireData(values, formSchema);
    const errs = validateSubmitData(wire, formSchema);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setBanner(t("public_intake.form.versioned.fix_errors"));
      return;
    }
    if (!consent) {
      setFieldErrors({});
      return; // ConsentCheckbox affiche l'erreur inline via showError
    }
    setFieldErrors({});

    submit.mutate(
      {
        data: wire,
        consent: { accepted: true, timestamp: new Date().toISOString() },
        submission_id: submissionIdRef.current,
      },
      {
        onSuccess: dispatch,
        onError: () => setBanner(t("public_intake.form.versioned.generic_error")),
      },
    );
  };

  const sections = useMemo(() => renderSchema.sections ?? [], [renderSchema]);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {t(signingMandate ? "flow_clarity.sign_mandate" : "public_intake.form.versioned.title")}
        </h1>
        <p className="text-sm text-slate-500">{t("public_intake.form.versioned.subtitle")}</p>
      </header>

      {banner && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {banner}
        </div>
      )}

      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        className="space-y-6"
      >
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
          {sections.length === 0 ? (
            <p className="text-sm text-slate-500">
              {t("public_intake.form.versioned.empty_schema")}
            </p>
          ) : (
            <FormRenderer
              schema={renderSchema}
              locale={language}
              mode="fill"
              values={values}
              onValuesChange={(next) => {
                setValues(next);
                if (attempted) {
                  setFieldErrors(validateSubmitData(toWireData(next, formSchema), formSchema));
                  setBanner(null);
                }
              }}
              errors={messagesDIssues(fieldErrors, t)}
            />
          )}
        </div>

        <ConsentCheckbox
          checked={consent}
          onChange={setConsent}
          disabled={busy}
          showError={attempted && !consent}
        />

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {busy
              ? t("public_intake.form.submitting")
              : t(signingMandate ? "flow_clarity.sign_mandate" : "public_intake.form.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
